import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Input } from '@enechain/ecloud-designsystem'

import { AuditPanel } from '~/components/common/AuditPanel'
import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { DsSelect, type DsSelectOptions } from '~/components/common/DsSelect'
import { FormGrid, GridLabel } from '~/pages/CustomerInfo/components/FormField'
import { FormModalShell } from '~/pages/CustomerInfo/components/FormModalShell'
import {
  useListContractors,
  useReloadCustomerInfo,
} from '~/pages/CustomerInfo/hooks/useCustomerInfoQueries'
import type { CustomerSummary } from '~/pages/CustomerInfo/models/types'

/** 需要家名の最大文字数 (モックの `CustomerContractCsv.CustomerNameMaxLength`、 server の検査と同じ)。 */
const CUSTOMER_NAME_MAX_LENGTH = 30

/** 保存の結果: 保存できた (undefined)・競合・上部に出す拒否の文言。 */
export type SaveResult = undefined | { conflict: true } | { error: string }

type Props = {
  customer: CustomerSummary
  isPending: boolean
  onClose: () => void
  /** 保存。 入力不備・競合以外の拒否の理由を返すと上部に出す (保存できたら undefined)。競合は呼び出し側が conflict を返す。 */
  onSave: (name: string, contractorId: string) => Promise<SaveResult>
}

/**
 * 需要家の編集モーダル。 項目は需要家名 (必須・30 文字以内。 同名の需要家は許す) と契約者
 * (選択肢は新規契約と同じ「<名前> (需要家 N 件)」。 付け替えると元の契約者の配下が減る)。
 * 契約は触らない。 他の人が先に保存していた時は、 契約の編集と同じ作法で競合を知らせて
 * 再読み込みへ促す。 作成・更新の記録を下部に出す。
 */
export const CustomerFormModal = ({
  customer,
  isPending,
  onClose,
  onSave,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const reload = useReloadCustomerInfo()
  const { data: contractors } = useListContractors()
  const [name, setName] = useState(customer.customerName)
  const [contractorId, setContractorId] = useState(customer.contractorId)
  const [isDirty, setIsDirty] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const [isConflict, setIsConflict] = useState(false)

  const handleSave = (): void => {
    void (async () => {
      const result = await onSave(name, contractorId)
      if (result === undefined) return
      if ('conflict' in result) {
        setIsConflict(true)
        return
      }
      setError(result.error)
    })()
  }

  const contractorOptions: DsSelectOptions = [...(contractors ?? [])]
    .sort((a, b) => a.contractor.name.localeCompare(b.contractor.name, 'ja'))
    .map((row) => ({
      value: row.contractor.id,
      label: t('page:customerInfo.form.contractorOption', {
        name: row.contractor.name,
        count: row.customerCount,
      }),
    }))

  return (
    <>
      <FormModalShell
        errorMessage={error}
        isDirty={isDirty}
        isPending={isPending}
        onClose={onClose}
        onSave={handleSave}
        saveLabel={t('common:action.save')}
        title={t('page:customerInfo.list.modal.editTitle')}
      >
        <FormGrid>
          <GridLabel htmlFor="customer-info-customer-field-name">
            {t('page:customerInfo.list.field.customerName')}
          </GridLabel>
          <Input
            id="customer-info-customer-field-name"
            maxLength={CUSTOMER_NAME_MAX_LENGTH}
            onChange={(e) => {
              setIsDirty(true)
              setError(undefined)
              setName(e.target.value)
            }}
            placeholder={t('page:customerInfo.list.field.customerName')}
            value={name}
          />
          <GridLabel htmlFor="customer-info-customer-field-contractor">
            {t('page:customerInfo.form.contractor')}
          </GridLabel>
          <DsSelect
            inputId="customer-info-customer-field-contractor"
            onChange={(value) => {
              if (value === undefined) return
              setIsDirty(true)
              setError(undefined)
              setContractorId(value)
            }}
            options={contractorOptions}
            value={contractorId}
          />
        </FormGrid>
        <AuditPanel
          createdAt={customer.createdAt}
          createdBy={customer.createdBy}
          updatedAt={customer.updatedAt}
          updatedBy={customer.updatedBy}
        />
      </FormModalShell>
      <ConfirmDialog
        body={t('page:customerInfo.confirm.customerConflictBody')}
        confirmLabel={t('page:customerInfo.confirm.conflictConfirm')}
        isOpen={isConflict}
        onCancel={() => setIsConflict(false)}
        onConfirm={() => {
          void reload()
          onClose()
        }}
        title={t('page:customerInfo.confirm.conflictTitle')}
      />
    </>
  )
}
